/**
 * process-reminders.ts — Berechtigungskonzept V4.0 · P7 Prozess-Erinnerungen (Stefan point 7).
 *
 * REWORKED from the V3.0 skeleton (ONE global tenant config) to Stefan's per-PROCESS 3-level model:
 *   "Pro Prozessschritt sollen 3 Stufen von Erinnerungen ausgeloest werden koennen." The Fristen are
 *   per-process different (urgency / sensitivity / importance differ), so the config is keyed on the
 *   PROCESS TYPE, not global.
 *
 * THE 3 LEVELS (per process type):
 *   1. Erinnerung per E-Mail ODER ins Portal (je nach Einstellung) nach z. B. 5 Tagen.
 *   2. Erinnerung per E-Mail ODER ins Portal (je nach Einstellung) nach z. B. 14 Tagen.
 *   3. Stufe: automatisch eine Aufgabe beim Kundenberater erstellen, dass er den Kunden bezueglich
 *      der offenen Pendenz kontaktieren soll (NICHT eine weitere Erinnerung).
 *   + ein dynamisches Template fuer alle Erinnerungen (leicht individualisiert, automatisch verschickt).
 *
 * ---------------------------------------------------------------------------------------------
 * PROCESS-TYPE KEY (decision, documented — confirm with Stefan/Valerio):
 *   Brokly types every open Pendenz by `task.process` (the `task_process` PG enum — 17 values:
 *   email_intake, invoice_check, mandate_signing, renewal_due, …). That IS the process taxonomy, so
 *   the reminder config is keyed on it (process_typ = the task_process value). A per-tenant config row
 *   per known process type carries the 3 Fristen + the two reminder channels + the level-3 action.
 * ADVISOR (level-3 "Kundenberater", documented — confirm):
 *   Brokly has NO dedicated customer-Betreuer field on tasks or customers (only policy.broker_owner,
 *   which is policy-level). The process's responsible advisor is therefore `task.assigned_to`
 *   (fallback: its assigned Funktionsgruppe). Level 3 creates the advisor task for that owner/group.
 * ---------------------------------------------------------------------------------------------
 *
 * [Source: packages/db/migrations/0000_init.sql] (the task_process enum — the process taxonomy)
 * [Source: projects/broker-ag/berechtigungen/ANALYSIS-v4.0-stefan-answers.md#P7] (Stefan point 7)
 */

/** The three reminder levels. Level 1 fires first, level 3 last (= the advisor-task escalation). */
export const PROCESS_REMINDER_LEVELS = [1, 2, 3] as const;

export type ProcessReminderLevel = (typeof PROCESS_REMINDER_LEVELS)[number];

/** The two configurable reminder channels for levels 1 + 2 (Stefan: "per E-Mail oder ins Portal"). */
export const PROCESS_REMINDER_KANAELE = ['email', 'portal'] as const;

export type ProcessReminderKanal = (typeof PROCESS_REMINDER_KANAELE)[number];

/**
 * Level-3 action types. V1 has exactly one: create an advisor task ("Aufgabe beim Kundenberater").
 * Modelled as an enum-like whitelist so a future level-3 action (e.g. a phone-call reminder) is a
 * one-line addition without a schema change (the column is text).
 */
export const PROCESS_REMINDER_L3_ACTIONS = ['advisor_task'] as const;

export type ProcessReminderL3Action = (typeof PROCESS_REMINDER_L3_ACTIONS)[number];

/**
 * The known `task_process` values a config row is boot-seeded for (the process taxonomy). A process
 * type NOT in this list (a future ALTER TYPE … ADD VALUE) simply falls back to the shared defaults in
 * the scanner until it is seeded — no breakage. Kept in sync with the task_process enum
 * (0000_init + the ADD VALUE migrations 0024/0025/0027/0031/0038/0053/0063/0132).
 */
export const PROCESS_REMINDER_PROCESS_TYPES = [
  'email_intake',
  'org_genesis',
  'zefix_enrich',
  'mandate_signing',
  'status_change',
  'doc_classification',
  'invoice_check',
  'invoice_manual_match',
  'invoice_k2_review',
  'invoice_k3_escalation',
  'commission_manual_match',
  'policy_endorsement_review',
  'renewal_due',
  'portal_message',
  'portal_mutation',
  'portal_claim',
  'document_upload',
  'other',
] as const;

export type ProcessReminderProcessType = (typeof PROCESS_REMINDER_PROCESS_TYPES)[number];

/** German UI labels per process type (code stays English; UI only). */
export const PROCESS_REMINDER_PROCESS_LABELS: Record<ProcessReminderProcessType, string> = {
  email_intake: 'E-Mail-Eingang',
  org_genesis: 'Firmen-Anlage',
  zefix_enrich: 'Zefix-Anreicherung',
  mandate_signing: 'Mandatsunterzeichnung',
  status_change: 'Statusaenderung',
  doc_classification: 'Dokumentklassifizierung',
  invoice_check: 'Rechnungskontrolle',
  invoice_manual_match: 'Rechnung manuell zuordnen',
  invoice_k2_review: 'Rechnung K2-Pruefung',
  invoice_k3_escalation: 'Rechnung K3-Eskalation',
  commission_manual_match: 'Courtage manuell zuordnen',
  policy_endorsement_review: 'Nachtrag-Pruefung',
  renewal_due: 'Erneuerung faellig',
  portal_message: 'Portal-Nachricht',
  portal_mutation: 'Portal-Mutation',
  portal_claim: 'Portal-Schadenmeldung',
  document_upload: 'Dokument-Upload',
  other: 'Sonstige',
};

/**
 * The per-process-type default Fristen (days after a process opened at which each level fires). These
 * are the sensible STARTING point the boot seed writes per known process type AND the scanner falls
 * back to for any un-seeded process type. Stefan: the Fristen are per-process DIFFERENT in production
 * (urgency/sensitivity/importance) — a broker tunes each type in Administration. 5 / 14 days for the
 * two reminders; day 30 for the advisor-task escalation.
 */
export const PROCESS_REMINDER_DEFAULT_LEVEL_DAYS: Record<ProcessReminderLevel, number> = {
  1: 5,
  2: 14,
  3: 30,
};

/** Default channel per reminder level (levels 1 + 2 only; level 3 is an action, not a channel). */
export const PROCESS_REMINDER_DEFAULT_KANAL: Record<1 | 2, ProcessReminderKanal> = {
  1: 'email',
  2: 'portal',
};

/** The default (and only, in V1) level-3 action. */
export const PROCESS_REMINDER_DEFAULT_L3_ACTION: ProcessReminderL3Action = 'advisor_task';

/**
 * The `enabled` a freshly-seeded CUSTOMER-RELEVANT process-type config row gets. The single boolean
 * that isProcessReminderDefaultEnabled resolves to for a customer-relevant type (an internal type
 * resolves to `false`). See PROCESS_REMINDER_CUSTOMER_RELEVANT_PROCESS_TYPES.
 */
export const PROCESS_REMINDER_DEFAULT_ENABLED = true;

/**
 * The CUSTOMER-RELEVANT process types — the ONLY types whose reminders default to ENABLED at
 * boot-seed. Rationale: the P7 level-1/2 reminders are CUSTOMER-FACING — they nudge the Kunde (VN)
 * about an open Pendenz on the CUSTOMER'S side, delivered into the customer portal or by e-mail
 * (see process-reminder-scan.service.ts). Enabling a reminder for an INTERNAL / automated broker
 * process (doc classification, invoice control, Zefix enrichment, ...) would nudge the CUSTOMER
 * about broker-internal work, which is wrong. So only the types where the CUSTOMER legitimately has
 * an open Pendenz they must act on default ON; every other (internal / automated, or already-
 * submitted customer inbound) type defaults OFF. A broker can still toggle ANY type per tenant in
 * Administration — this only sets the SEED default.
 *
 * CLASSIFICATION of the 17 task_process types (concept point 7 examples: mandate / signature /
 * renewal / document-return = customer-relevant):
 *   CUSTOMER-RELEVANT (default ENABLED) — the customer must act to close the Pendenz:
 *     - mandate_signing — the customer signs the broker mandate (mandate + signature).
 *     - renewal_due     — a renewal decision is due on the customer's side (renewal).
 *   INTERNAL / automated / already-submitted (default DISABLED) — the BROKER or SYSTEM acts, or the
 *   customer has already submitted and only broker processing remains:
 *     email_intake, org_genesis, zefix_enrich, status_change, doc_classification, invoice_check,
 *     invoice_manual_match, invoice_k2_review, invoice_k3_escalation, commission_manual_match,
 *     policy_endorsement_review  — broker / system work.
 *     portal_message, portal_mutation, portal_claim  — Kunde->Broker inbound: the customer has
 *       already submitted, the OPEN Pendenz is the broker's "Pruefung und Uebernahme durch den
 *       Broker" (see portal-intake.service.ts) — nudging the customer would be wrong.
 *     other  — the manual / follow-up catch-all (also the level-3 advisor-task lane) -> off by default.
 *
 * An ALLOW-LIST (not a deny-list) so a future ADD VALUE process type fails CLOSED (defaults OFF, no
 * accidental customer nudge) until it is explicitly classified customer-relevant here.
 */
export const PROCESS_REMINDER_CUSTOMER_RELEVANT_PROCESS_TYPES = [
  'mandate_signing',
  'renewal_due',
] as const satisfies readonly ProcessReminderProcessType[];

const PROCESS_REMINDER_CUSTOMER_RELEVANT_SET: ReadonlySet<string> = new Set(
  PROCESS_REMINDER_CUSTOMER_RELEVANT_PROCESS_TYPES,
);

/**
 * The boot-seed default `enabled` for one process type: ENABLED only for a CUSTOMER-RELEVANT type
 * (the customer-facing L1/L2 reminder legitimately nudges the customer), DISABLED for every
 * internal / automated / already-submitted type. Pure; the SINGLE source of truth the seed AND the
 * config-read fallback both call, so the seed and the Administration UI never drift.
 */
export function isProcessReminderDefaultEnabled(processTyp: string): boolean {
  return PROCESS_REMINDER_CUSTOMER_RELEVANT_SET.has(processTyp)
    ? PROCESS_REMINDER_DEFAULT_ENABLED
    : false;
}

/** German level labels (UI only). */
export const PROCESS_REMINDER_LEVEL_LABELS: Record<ProcessReminderLevel, string> = {
  1: '1. Erinnerung',
  2: '2. Erinnerung',
  3: '3. Stufe: Aufgabe an Kundenberater',
};

// ── Dynamic reminder template ───────────────────────────────────────────────────

/**
 * The whitelisted placeholders a reminder template may use. Only these are substituted; any other
 * `{{token}}` is left literal (never inject arbitrary data). Values are plain text — escaped
 * downstream (renderBrandedEmail HTML-escapes; the in-app message is rendered by React which escapes).
 */
export const PROCESS_REMINDER_TEMPLATE_PLACEHOLDERS = [
  'kunde',
  'vorgang',
  'frist',
  'tage',
  'stufe',
] as const;

export type ProcessReminderTemplatePlaceholder = (typeof PROCESS_REMINDER_TEMPLATE_PLACEHOLDERS)[number];

/** The per-tenant reminder template (one per tenant; seeded from this default). */
export const PROCESS_REMINDER_DEFAULT_TEMPLATE = {
  subject: 'Erinnerung: offener Vorgang {{vorgang}} ({{stufe}}. Stufe)',
  body:
    'Guten Tag\n\n' +
    'der Vorgang "{{vorgang}}" fuer {{kunde}} ist seit {{tage}} Tagen offen und hat die {{stufe}}. ' +
    'Erinnerungsstufe (Frist {{frist}} Tage) erreicht.\n\n' +
    'Bitte pruefen Sie den Vorgang in Brokly und schliessen Sie ihn ab, sobald er erledigt ist.\n\n' +
    'Freundliche Gruesse\nBrokly',
} as const;

/**
 * Fill a reminder template — pure, whitelist-only. Substitutes ONLY the known placeholders
 * ({{kunde}}, {{vorgang}}, {{frist}}, {{tage}}, {{stufe}}); an unknown `{{token}}` is left literal.
 * A missing value renders empty. This is the single template-fill seam shared by the api (per-reminder
 * rendering) and its tests.
 */
export function renderReminderTemplate(
  template: { subject: string; body: string },
  values: Partial<Record<ProcessReminderTemplatePlaceholder, string | number>>,
): { subject: string; body: string } {
  const whitelist = PROCESS_REMINDER_TEMPLATE_PLACEHOLDERS as readonly string[];
  const fill = (text: string): string =>
    text.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (match, rawToken: string) => {
      const token = rawToken.toLowerCase();
      if (!whitelist.includes(token)) return match; // unknown token → leave literal
      const value = values[token as ProcessReminderTemplatePlaceholder];
      return value === undefined || value === null ? '' : String(value);
    });
  return { subject: fill(template.subject), body: fill(template.body) };
}

// ── Wire contracts ──────────────────────────────────────────────────────────────

/** One process-type's reminder config (part of the GET /admin/process-reminders response). */
export interface ProcessReminderTypeConfigDto {
  /** The task_process value this config keys on (e.g. 'invoice_check'). */
  process_typ: string;
  /** German label for the UI. */
  process_label: string;
  /** Whether reminders are active for this process type. */
  enabled: boolean;
  /** Days after a process opened at which level 1 fires. */
  level_1_days: number;
  /** Level-1 channel: email OR portal. */
  level_1_kanal: ProcessReminderKanal;
  /** Days after a process opened at which level 2 fires. */
  level_2_days: number;
  /** Level-2 channel: email OR portal. */
  level_2_kanal: ProcessReminderKanal;
  /** Days after a process opened at which level 3 (the advisor-task escalation) fires. */
  level_3_days: number;
  /** Level-3 action (V1: always 'advisor_task'). */
  level_3_action: ProcessReminderL3Action;
}

/** The per-tenant reminder template. */
export interface ProcessReminderTemplateDto {
  subject: string;
  body: string;
}

/** GET /admin/process-reminders response — every known process type + the tenant's template. */
export interface ProcessReminderConfigResponseDto {
  process_types: ProcessReminderTypeConfigDto[];
  template: ProcessReminderTemplateDto;
}

/**
 * PUT /admin/process-reminders/type body — upsert one process type's config.
 *
 * RETIRED (P7 channel rework): level_1_kanal / level_2_kanal are no longer set by the UI. The L1/L2
 * reminders are CUSTOMER-FACING and the channel is chosen by a RULE at send time — active customer
 * portal access → the portal, otherwise e-mail — NOT by a per-level toggle. The two fields stay here
 * OPTIONAL only for backward compatibility (the columns are kept but no longer read by the scanner);
 * omit them. See process-reminder-scan.service.ts.
 */
export interface SetProcessReminderTypeConfigInput {
  process_typ: string;
  enabled: boolean;
  level_1_days: number;
  /** @deprecated retired — the L1/L2 channel is portal-else-email (customer-facing), not a toggle. */
  level_1_kanal?: ProcessReminderKanal;
  level_2_days: number;
  /** @deprecated retired — the L1/L2 channel is portal-else-email (customer-facing), not a toggle. */
  level_2_kanal?: ProcessReminderKanal;
  level_3_days: number;
}

/** PUT /admin/process-reminders/template body — set the dynamic template. */
export interface SetProcessReminderTemplateInput {
  subject: string;
  body: string;
}
